import "swiper/css";
import { useMemo } from "react";
import { BrowserRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createTheme } from "@mui/material/styles";
import { frFR as materialFr, enUS as materialEn } from "@mui/material/locale";
import { frFR as gridFr, enUS as gridEn } from "@mui/x-data-grid";
import { frFR as pickersFr, enUS as pickersEn } from "@mui/x-date-pickers/locales";
import "./App.css";
import { Provider } from "react-redux";
import store from "./store/store";
import { createAppTheme } from "./theme";
import { ThemeModeProvider, useThemeMode } from "./theme/ThemeModeContext";
import { useThemeSync } from "./theme/useThemeSync";
import { useLanguageSync } from "./i18n/useLanguageSync";

import Animatedroutes from "./Components/Router/Animatedroutes";
import LoadMap from "./Components/LoadMap";
import CustomSnackbar from "./Components/Popups/CustomSnackBar";
import PublicLanguageSwitch from "./Components/LanguageSwitch/PublicLanguageSwitch";
import { normalizeLanguage } from "./i18n/languages";

import { ThemeProvider, CssBaseline } from "@mui/material";
import AppOverlay from "./Components/Common/AppOverlay";

const AppShell = () => {
  const { resolvedTheme } = useThemeMode();
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  useThemeSync();
  useLanguageSync();

  // MUI's own words (pagination, pickers, data grid) follow the website's language.
  const muiTheme = useMemo(
    () =>
      createTheme(
        createAppTheme(resolvedTheme),
        ...(language === "fr"
          ? [materialFr, gridFr, pickersFr]
          : [materialEn, gridEn, pickersEn]),
      ),
    [resolvedTheme, language],
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <BrowserRouter>
        <AppOverlay />
        <LoadMap />
        <CustomSnackbar />
        <PublicLanguageSwitch />
        <Animatedroutes />
      </BrowserRouter>
    </ThemeProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeModeProvider>
        <AppShell />
      </ThemeModeProvider>
    </Provider>
  );
}

export default App;
